import { useMemo, useState, useEffect, type ReactNode } from "react"
import { ArrowLeft, Check, Info, Loader2, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import { Footer } from "@/components/Footer"
import { Input } from "@/components/ui/input"
import { useBooking } from "@/context/BookingContext"
import { createBooking, ApiError, AuthError } from "@/lib/api/client"
import { dispatchApiError } from "@/lib/api/dispatch"
import { toAsyncError } from "@/lib/api/errors"
import type { AsyncError } from "@/lib/api/errors"
import type { BookingRequestDTO, BookingType, PayLaterOption } from "@/lib/api/types"
import { searchFlightOffers } from "@/lib/api/search"
import { eventPath } from "@/lib/utils"
import {
  formatDateRange,
  formatMoney,
  getActivePaymentOption,
  getAddOnsTotal,
  getGrandTotal,
  getPackagePricePerPerson,
  getSelectedAddOns,
  getSelectedStayPeriod,
} from "@/lib/booking"
import { PayLaterInfoModal } from "@/components/PayLaterInfoModal"
import { AddOnNoteMedia } from "@/components/AddOnNoteMedia"
import { lookupCityCode } from "@/lib/airports"
import { pushBeginCheckout, savePurchasePayload } from "@/lib/ga4"
import { phCapture, phIdentify } from "@/lib/posthog"

type FieldKey = "firstName" | "lastName" | "email" | "phone"

function validateCheckoutFields(travelerInfo: { firstName: string; lastName: string; email: string; phone: string }): Partial<Record<FieldKey, string>> {
  const errors: Partial<Record<FieldKey, string>> = {}
  if (travelerInfo.firstName.trim() === "") errors.firstName = "First name is required."
  if (travelerInfo.lastName.trim() === "") errors.lastName = "Last name is required."
  if (travelerInfo.email.trim() === "") errors.email = "Email is required."
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(travelerInfo.email)) errors.email = "Please enter a valid email address."
  if (travelerInfo.phone.trim() === "") errors.phone = "Phone number is required."
  else if (!/^\+\d/.test(travelerInfo.phone.trim())) errors.phone = "Phone number must include a country code (e.g., +421)."
  return errors
}

export default function Checkout() {
  const { state, dispatch } = useBooking()
  const [payLaterEnabled, setPayLaterEnabled] = useState(false)
  const [payLaterInfoOpen, setPayLaterInfoOpen] = useState(false)
  const [subscribe, setSubscribe] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState(false)
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const fieldErrors = validateCheckoutFields(state.travelerInfo)
  const hasFieldErrors = Object.keys(fieldErrors).length > 0

  const selectedStayPeriod = getSelectedStayPeriod(state)
  const hasCustomFlightDates =
    state.packageType === "flight-only" &&
    !!state.customFlightDepartureDate &&
    !!state.customFlightReturnDate
  const hasEffectiveDates = !!selectedStayPeriod || hasCustomFlightDates
  const selectedAddOns = getSelectedAddOns(state)
  const packagePricePerPerson = getPackagePricePerPerson(state)
  const extrasTotal = getAddOnsTotal(state)
  const grandTotal = getGrandTotal(state)
  const packageOnlyTotal = Math.max(grandTotal - extrasTotal, 0)
  const payLaterOption = state.offers?.payLater ?? null
  const eventSlug = state.event?.slug ?? undefined
  const termsHref = eventPath(eventSlug, "terms")
  const privacyHref = eventPath(eventSlug, "privacy")
  const selectedFlightDestination = state.selectedFlight
    ? (lookupCityCode(state.selectedFlight.outbound.destinationAirportCode)?.city ??
      state.destinationAirport?.city ??
      (state.event?.airport ? lookupCityCode(state.event.airport)?.city : undefined) ??
      state.event?.airport ??
      state.selectedFlight.outbound.destinationAirportCode)
    : null
  const isSelectionComplete =
    hasEffectiveDates &&
    (state.packageType === "flight-only"
      ? state.selectedFlight !== null && state.flightAllotmentId !== null
      : state.selectedHotel !== null &&
        (state.packageType === "hotel-only" || state.selectedFlight !== null))

  const bookingPayload = useMemo<BookingRequestDTO | null>(() => {
    if (!hasEffectiveDates) return null

    if (state.packageType === "flight-only") {
      if (!state.selectedFlight || !state.flightAllotmentId) return null

      return {
        selectedAddOns: selectedAddOns.map(({ addOn, quantity }) => ({
          addOnId: addOn.id,
          quantity,
        })),
        allotmentId: state.flightAllotmentId,
        passengerCount: state.passengerCount,
        contactEmail: state.travelerInfo.email,
        contactName: state.travelerInfo.firstName,
        contactSurname: state.travelerInfo.lastName,
        contactPhone: state.travelerInfo.phone.trim() || null,
        departureAirport: state.selectedFlight.departureAirport.code,
        flightSnapshotKey: state.selectedFlight.id,
        bookingType: "flight" as BookingType,
        paymentOption: getActivePaymentOption(state, payLaterEnabled),
        bookShared: false,
      }
    }

    if (!state.selectedHotel) return null
    if (state.packageType === "flight-hotel" && !state.selectedFlight)
      return null

    const bookingType: BookingType =
      state.packageType === "flight-hotel" ? "hotel_flight" : "hotel"

    return {
      selectedAddOns: selectedAddOns.map(({ addOn, quantity }) => ({
        addOnId: addOn.id,
        quantity,
      })),
      allotmentId: state.selectedHotel.allotmentId,
      passengerCount: state.passengerCount,
      contactEmail: state.travelerInfo.email,
      contactName: state.travelerInfo.firstName,
      contactSurname: state.travelerInfo.lastName,
      contactPhone: state.travelerInfo.phone.trim() || null,
      departureAirport:
        state.packageType === "flight-hotel"
          ? (state.selectedFlight?.departureAirport.code ?? null)
          : null,
      flightSnapshotKey:
        state.packageType === "flight-hotel"
          ? (state.selectedFlight?.id ?? null)
          : null,
      bookingType,
      paymentOption: getActivePaymentOption(state, payLaterEnabled),
      bookShared: state.selectedHotel.shared,
    }
  }, [payLaterEnabled, selectedAddOns, selectedStayPeriod, state])

  // GA4: begin_checkout — fires once when the Review and Checkout step is displayed.
  // The Checkout component only mounts when state.currentStep === "checkout" (App.tsx:162),
  // so an empty-deps effect fires exactly once per checkout session.
  useEffect(() => {
    if (!state.selectedFlight || !isSelectionComplete) return
    pushBeginCheckout(
      state.selectedFlight,
      state.flightBasePackagePrice ?? state.offers?.basePackagePricePerPerson ?? 0,
      state.passengerCount,
      state.currency,
      grandTotal,
      state.event?.slug ?? "",
      state.event?.name ?? ""
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally empty — fire once on mount only

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    setSubmitAttempted(true)
    setTouched({ firstName: true, lastName: true, email: true, phone: true })

    if (!bookingPayload || !isSelectionComplete) {
      toast.error("Please complete your booking details first.")
      return
    }

    if (hasFieldErrors) return

    if (!termsAccepted) {
      setTermsError(true)
      return
    }

    dispatch({ type: "SET_BOOKING_SUBMITTING", submitting: true })
    dispatch({ type: "SET_BOOKING_ERROR", error: null })

    try {
      const response = await createBooking(bookingPayload)

      try {
        const exponea = (window as any).exponea
        if (exponea) {
          exponea.identify(
            { registered: state.travelerInfo.email },
            {
              email: state.travelerInfo.email,
              first_name: state.travelerInfo.firstName,
              last_name: state.travelerInfo.lastName,
              phone: state.travelerInfo.phone,
            }
          )

          if (subscribe) {
            exponea.track('subscribe', {
              email: state.travelerInfo.email,
              source: 'checkout_newsletter_checkbox',
              event_name: state.event?.name ?? '',
            })
          }
        }
      } catch { /* fire and continue */ }

      // GA4: persist purchase payload to sessionStorage so it survives the
      // external Stripe redirect and can be fired on the success page.
      if (state.selectedFlight) {
        savePurchasePayload({
          bookingId: response.bookingId,
          currency: state.currency,
          value: grandTotal,
          flight: state.selectedFlight,
          eventSlug: state.event?.slug ?? "",
          eventName: state.event?.name ?? "",
          passengerCount: state.passengerCount,
          basePackagePrice:
            state.flightBasePackagePrice ??
            state.offers?.basePackagePricePerPerson ??
            0,
          travelerInfo: state.travelerInfo,
          paymentOption: getActivePaymentOption(state, payLaterEnabled),
        })
      }

      // PostHog: identify the user so pre-booking anonymous activity is merged
      // with the named profile — called here where bookingId and full state
      // are in scope, before the hard redirect to Stripe.
      phIdentify(state.travelerInfo.email, {
        first_name: state.travelerInfo.firstName,
        last_name: state.travelerInfo.lastName,
        phone: state.travelerInfo.phone,
      })
      // PostHog: booking_created
      phCapture('booking_created', {
        booking_id: response.bookingId,
        event_slug: state.event?.slug ?? "",
        package_type: state.packageType,
        passenger_count: state.passengerCount,
        currency: state.currency,
        value: grandTotal,
        payment_option: getActivePaymentOption(state, payLaterEnabled),
      })

      window.location.href = response.checkoutUrl
    } catch (error) {
      if (error instanceof AuthError) {
        window.location.href = "/login"
        return
      }

      // The counterpart to booking_created above. Without it a booking that
      // never gets created leaves no trace at all: the error is handled here,
      // so it never reaches exception autocapture either. Carries the status
      // so a sold-out 409 can be told apart from an upstream failure.
      phCapture('booking_failed', {
        event_slug: state.event?.slug ?? "",
        package_type: state.packageType,
        passenger_count: state.passengerCount,
        payment_option: getActivePaymentOption(state, payLaterEnabled),
        status: error instanceof ApiError ? error.status : null,
        error_type: error instanceof ApiError ? "api" : "network",
      })

      if (error instanceof ApiError && error.status === 409) {
        const debugMsg = (error as ApiError).debugInfo ?? ""
        const isFlightExpired =
          debugMsg.includes("flight is no longer available") ||
          debugMsg.includes("reselect") ||
          debugMsg.includes("fresh offer")

        if (isFlightExpired && (state.packageType === "flight-hotel" || state.packageType === "flight-only")) {
          toast.error("The chosen flight is no longer available. Please select a new flight.")
          const stayPeriod = getSelectedStayPeriod(state)
          const hasCustomDates =
            state.packageType === "flight-only" &&
            state.customFlightDepartureDate &&
            state.customFlightReturnDate

          if (hasCustomDates || stayPeriod) {
            const stayStartDate = hasCustomDates
              ? state.customFlightDepartureDate!
              : stayPeriod!.stayStartDate
            const stayEndDate = hasCustomDates
              ? state.customFlightReturnDate!
              : stayPeriod!.stayEndDate

            try {
              const flightResult = await searchFlightOffers({
                eventId: state.event?.id ?? "",
                stayStartDate,
                stayEndDate,
                passengerCount: state.passengerCount,
                departureAirports: state.departureAirports.map((a) => a.code),
                destination: state.destinationAirport?.code ?? undefined,
                mode: state.packageType === "flight-only" ? "flight" : "hotel_flight",
              })
              dispatch({
                type: "SET_FLIGHT_OFFERS_SUCCESS",
                flightOffers: flightResult.flightOffers,
                flightOptions: flightResult.flightOptions,
              })
            } catch (refreshError) {
              dispatchApiError(dispatch, refreshError, "flights")
            }
          }
          dispatch({ type: "SET_STEP", step: "flight-selection" })
          return
        }

        toast.error(
          "Availability has changed. Please select again from the updated offers."
        )
        dispatch({ type: "REFRESH_NEEDED" })
        dispatch({ type: "SET_STEP", step: state.packageType === "flight-only" ? "flight-selection" : "hotel-selection" })
        return
      }

      const asyncError = toAsyncError(error, "booking")
      dispatch({ type: "SET_BOOKING_ERROR", error: asyncError })
    } finally {
      dispatch({ type: "SET_BOOKING_SUBMITTING", submitting: false })
    }
  }

  function handleBack() {
    dispatch({ type: "SET_STEP", step: "addons" })
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (!isSelectionComplete || !hasEffectiveDates) {
    return (
      <div className="min-h-screen">
        <div className="border-b border-border">
          <div className="mx-auto max-w-7xl px-4 py-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              Back to booking
            </Button>
          </div>
        </div>

        <div className="mx-auto flex min-h-[70vh] max-w-4xl items-center justify-center px-4 text-center">
          <div className="rounded-xl border border-border bg-card/70 p-8">
            <h1 className="text-2xl font-bold text-foreground">
              Complete your selection first
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {state.packageType === "flight-only"
                ? "Choose your flight before continuing to checkout."
                : "Choose your hotel and flight before continuing to checkout."}
            </p>
          </div>
        </div>

        <Footer eventSlug={eventSlug} />
      </div>
    )
  }

  const heroImage = state.selectedHotel?.images[0] ?? null
  const dateLabel = formatDateRange(
    selectedStayPeriod?.stayStartDate ?? state.customFlightDepartureDate,
    selectedStayPeriod?.stayEndDate ?? state.customFlightReturnDate
  )

  return (
    <div className="min-h-screen overflow-x-clip">
      <div className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back to booking
          </Button>
        </div>
      </div>

      <div className="h-[2px] bg-primary/40" />

      <form onSubmit={handleSubmit}>
        <div className="mx-auto max-w-7xl px-4 py-10 @min-[64rem]:grid @min-[64rem]:grid-cols-[1fr_340px] @min-[64rem]:gap-8">
          <div className="flex flex-col gap-6">
            <h1 className="text-3xl font-bold text-foreground">
              Review and Checkout
            </h1>

            <SurfaceCard title="Trip Details">
              <SummaryRow
                label="Destination"
                value={state.event?.location ?? "TBA"}
              />
              <SummaryRow label="Dates" value={dateLabel} />
              <SummaryRow
                label="Travelers"
                value={`${state.passengerCount} ${state.passengerCount === 1 ? "person" : "people"}`}
              />
            </SurfaceCard>

            <SurfaceCard
              title="Travel Package"
              icon={<PackageIcon packageType={state.packageType} />}
            >
              {state.selectedHotel && (
                <div className="flex items-start gap-4">
                  {heroImage ? (
                    <img
                      src={heroImage}
                      alt={`${state.selectedHotel.name} hotel preview`}
                      className="size-16 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-secondary text-xs text-muted-foreground">
                      Hotel
                    </div>
                  )}

                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground">Hotel</span>
                    <span className="text-sm font-semibold text-foreground">
                      {state.selectedHotel.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {state.selectedHotel.distanceFromVenue ||
                        "Venue distance on request"}
                    </span>
                    <div className="mt-1 flex items-center gap-1">
                      <Star className="size-3 fill-primary text-primary" />
                      <span className="text-xs font-medium text-primary">
                        {state.selectedHotel.stars}.0 stars
                      </span>
                    </div>

                    {/*
                      Last chance to see it before paying. It is not in any
                      total on this page, so it has to say where it is paid.
                    */}
                    {state.selectedHotel.localFee && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {state.selectedHotel.localFee} — paid at the hotel, not
                        included below
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(state.packageType === "flight-hotel" || state.packageType === "flight-only") && state.selectedFlight && (
                <div className={`${state.selectedHotel ? "mt-5 border-t border-border pt-4" : ""}`}>
                  <span className="text-xs text-muted-foreground">Flight</span>
                  <p className="text-sm font-semibold text-foreground">
                    {state.selectedFlight.departureAirport.city} -{" "}
                    {selectedFlightDestination}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {state.selectedFlight.outbound.departureTime} -{" "}
                    {state.selectedFlight.outbound.arrivalTime} -{" "}
                    {state.selectedFlight.outbound.travelClass}
                  </p>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                <span className="text-sm text-muted-foreground">
                  Package price / person
                </span>
                <span className="text-lg font-bold text-primary">
                  {formatMoney(packagePricePerPerson, state.currency)}
                </span>
              </div>
              {state.passengerCount > 1 && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">
                    Total for {state.passengerCount} travelers
                  </span>
                  <span className="text-lg font-bold text-primary">
                    {formatMoney(
                      packagePricePerPerson * state.passengerCount,
                      state.currency
                    )}
                  </span>
                </div>
              )}
            </SurfaceCard>

            {selectedAddOns.length > 0 && (
              <SurfaceCard title="Extras">
                <div className="flex flex-col gap-4">
                  {selectedAddOns.map(({ addOn, quantity }) => (
                    <div
                      key={addOn.id}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="min-w-0 text-[14px] font-semibold text-foreground">
                        {addOn.name}
                        {addOn.note && (
                          <>
                            {" - "}
                            <AddOnNoteMedia note={addOn.note} label={addOn.name} />
                          </>
                        )}{" "}
                        x{quantity}
                      </span>
                      <span className="text-[18px] leading-none font-bold text-primary">
                        {formatMoney(
                          addOn.priceDeltaPerPerson * quantity,
                          state.currency
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </SurfaceCard>
            )}

            <SurfaceCard title="Contact Details" headingStyle="compact">
              <p className="mt-1 mb-5 text-sm text-muted-foreground">
                We will send your booking confirmation here
              </p>

              <div className="grid gap-4 @min-[40rem]:grid-cols-2">
                <Field label="First Name" required error={touched.firstName || submitAttempted ? fieldErrors.firstName : undefined}>
                  <Input
                    placeholder="John"
                    value={state.travelerInfo.firstName}
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_TRAVELER_INFO",
                        info: { firstName: event.target.value },
                      })
                    }
                    onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
                    aria-invalid={(touched.firstName || submitAttempted) && fieldErrors.firstName ? true : undefined}
                    required
                  />
                </Field>

                <Field label="Last Name" required error={touched.lastName || submitAttempted ? fieldErrors.lastName : undefined}>
                  <Input
                    placeholder="Doe"
                    value={state.travelerInfo.lastName}
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_TRAVELER_INFO",
                        info: { lastName: event.target.value },
                      })
                    }
                    onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
                    aria-invalid={(touched.lastName || submitAttempted) && fieldErrors.lastName ? true : undefined}
                    required
                  />
                </Field>

                <Field label="Email" required error={touched.email || submitAttempted ? fieldErrors.email : undefined}>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={state.travelerInfo.email}
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_TRAVELER_INFO",
                        info: { email: event.target.value },
                      })
                    }
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    aria-invalid={(touched.email || submitAttempted) && fieldErrors.email ? true : undefined}
                    required
                  />
                </Field>

                <Field label="Phone" required error={touched.phone || submitAttempted ? fieldErrors.phone : undefined}>
                  <Input
                    type="tel"
                    placeholder="+421 900 000 000"
                    value={state.travelerInfo.phone}
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_TRAVELER_INFO",
                        info: { phone: event.target.value },
                      })
                    }
                    onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    aria-invalid={(touched.phone || submitAttempted) && fieldErrors.phone ? true : undefined}
                    required
                  />
                </Field>
              </div>
            </SurfaceCard>

            {payLaterOption && (
              <>
                <PayLaterInfoModal
                  open={payLaterInfoOpen}
                  onOpenChange={setPayLaterInfoOpen}
                  payLaterOption={payLaterOption}
                  currency={state.currency}
                />
                <div
                  className="flex items-center gap-3 rounded-xl border p-4 transition-all @min-[40rem]:gap-4 @min-[40rem]:p-5"
                  style={{
                    background: payLaterEnabled
                      ? "color-mix(in oklch, var(--primary) 6%, transparent)"
                      : "var(--muted)",
                    borderColor: payLaterEnabled
                      ? "var(--primary)"
                      : "var(--border)",
                  }}
                >
                  <Checkbox
                    className="size-6 shrink-0 rounded"
                    checked={payLaterEnabled}
                    onCheckedChange={(checked) => {
                      setPayLaterEnabled(!!checked)
                      dispatch({
                        type: "SET_PAYMENT_OPTION",
                        paymentOption: checked
                          ? payLaterOption.option
                          : "FULL",
                      })
                    }}
                  />

                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        Book now, pay later
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setPayLaterInfoOpen(true)}
                        className="size-6 rounded-full hover:bg-amber-400/15"
                        aria-label="Split payment info"
                      >
                        <Info className="size-4 text-amber-400" />
                      </Button>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {payLaterOption.installments} installments available for
                      this package.
                    </span>
                  </div>

                  <span className="shrink-0 text-sm font-bold text-primary">
                    {formatMoney(payLaterOption.depositAmount, state.currency)}
                  </span>
                </div>
              </>
            )}

            <SurfaceCard title="Payment Method">
              <div className="mb-4 flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-muted-foreground"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                <span className="text-sm font-semibold text-foreground">
                  Online Payment
                </span>
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                <PaymentBadge>Apple Pay</PaymentBadge>
                <PaymentBadge>Google Pay</PaymentBadge>
                <PaymentBadge>Visa</PaymentBadge>
                <PaymentBadge>Mastercard</PaymentBadge>
              </div>

              <p className="mb-6 text-sm text-muted-foreground">
                Pay securely using your preferred payment method. You will be
                redirected to the live checkout after booking creation.
              </p>

              {state.bookingError && (
                <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {state.bookingError.userMessage}
                </div>
              )}

              <ConsentCheckboxes
                subscribe={subscribe}
                setSubscribe={setSubscribe}
                termsAccepted={termsAccepted}
                setTermsAccepted={setTermsAccepted}
                termsError={termsError}
                setTermsError={setTermsError}
                termsHref={termsHref}
                privacyHref={privacyHref}
              />

              <Button
                type="submit"
                disabled={state.isSubmittingBooking}
                size="xl"
                className="w-full hidden @min-[64rem]:flex"
              >
                {state.isSubmittingBooking ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  "Continue to Payment"
                )}
              </Button>
            </SurfaceCard>
          </div>

          <div className="hidden @min-[64rem]:block">
            <div className="sticky top-6 mt-12">
              <PriceSummary
                packageOnlyTotal={packageOnlyTotal}
                grandTotal={grandTotal}
                selectedAddOns={selectedAddOns}
                payLaterEnabled={payLaterEnabled}
                payLaterOption={payLaterOption}
                currency={state.currency}
                hasFlight={state.packageType === "flight-hotel" || state.packageType === "flight-only"}
              />
            </div>
          </div>

          <div className="@min-[64rem]:hidden">
            <div className="mt-6">
              <PriceSummary
                packageOnlyTotal={packageOnlyTotal}
                grandTotal={grandTotal}
                selectedAddOns={selectedAddOns}
                payLaterEnabled={payLaterEnabled}
                payLaterOption={payLaterOption}
                currency={state.currency}
                hasFlight={state.packageType === "flight-hotel" || state.packageType === "flight-only"}
                showSubmitButton
                isSubmitting={state.isSubmittingBooking}
                bookingError={state.bookingError}
                subscribe={subscribe}
                setSubscribe={setSubscribe}
                termsAccepted={termsAccepted}
                setTermsAccepted={setTermsAccepted}
                termsError={termsError}
                setTermsError={setTermsError}
                termsHref={termsHref}
                privacyHref={privacyHref}
              />
            </div>
          </div>
        </div>
      </form>

      <Footer eventSlug={eventSlug} />
    </div>
  )
}

function PriceSummary({
  packageOnlyTotal,
  grandTotal,
  selectedAddOns,
  payLaterEnabled,
  payLaterOption,
  currency,
  hasFlight,
  showSubmitButton = false,
  isSubmitting = false,
  bookingError = null,
  subscribe,
  setSubscribe,
  termsAccepted,
  setTermsAccepted,
  termsError,
  setTermsError,
  termsHref,
  privacyHref,
}: {
  packageOnlyTotal: number
  grandTotal: number
  selectedAddOns: {
    addOn: { id: string; name: string | null; note: string | null; priceDeltaPerPerson: number }
    quantity: number
  }[]
  payLaterEnabled: boolean
  payLaterOption: PayLaterOption | null
  currency: string
  hasFlight?: boolean
  showSubmitButton?: boolean
  isSubmitting?: boolean
  bookingError?: AsyncError | null
  subscribe?: boolean
  setSubscribe?: (v: boolean) => void
  termsAccepted?: boolean
  setTermsAccepted?: (v: boolean) => void
  termsError?: boolean
  setTermsError?: (v: boolean) => void
  termsHref?: string
  privacyHref?: string
}) {
  return (
    <div className="rounded-xl border border-border bg-muted p-4 @min-[48rem]:p-6">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-lg font-semibold text-foreground">
          Price Summary
        </h3>
      </div>

      <div className="flex flex-col gap-3">
        <SummaryRow
          label="Travel Package"
          value={formatMoney(packageOnlyTotal, currency)}
        />

        {selectedAddOns.length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="mb-3">
              <span className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                Extras
              </span>
            </div>

            <div className="flex flex-col gap-3">
              {selectedAddOns.map(({ addOn, quantity }) => (
                <SummaryRow
                  key={addOn.id}
                  label={
                    <span>
                      {addOn.name} x{quantity}
                    </span>
                  }
                  value={formatMoney(
                    addOn.priceDeltaPerPerson * quantity,
                    currency
                  )}
                  muted
                />
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-border pt-3">
          <SummaryRow
            label="Subtotal"
            value={formatMoney(grandTotal, currency)}
            muted
          />
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-foreground">
              Total
            </span>
            <span className="text-2xl font-bold text-primary">
              {formatMoney(grandTotal, currency)}
            </span>
          </div>
        </div>

        {payLaterEnabled && payLaterOption && (
          <div
            className="rounded-lg p-3"
            style={{
              background: "color-mix(in oklch, var(--primary) 6%, transparent)",
              border: "1px solid color-mix(in oklch, var(--primary) 20%, transparent)",
            }}
          >
            <SummaryRow
              label="Due today"
              value={formatMoney(payLaterOption.depositAmount, currency)}
              muted
            />
            <div className="mt-1">
              <SummaryRow
                label="Due later"
                value={formatMoney(
                  Math.max(grandTotal - payLaterOption.depositAmount, 0),
                  currency
                )}
                muted
              />
            </div>
          </div>
        )}

        {showSubmitButton && (
          <div className="mt-4 flex flex-col gap-3">
            {bookingError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {bookingError.userMessage}
              </div>
            )}
            {subscribe !== undefined && setSubscribe && termsAccepted !== undefined && setTermsAccepted && termsError !== undefined && setTermsError && (
              <ConsentCheckboxes
                subscribe={subscribe}
                setSubscribe={setSubscribe}
                termsAccepted={termsAccepted}
                setTermsAccepted={setTermsAccepted}
                termsError={termsError}
                setTermsError={setTermsError}
                termsHref={termsHref ?? "/terms"}
                privacyHref={privacyHref ?? "/privacy"}
              />
            )}
            <Button
              type="submit"
              disabled={isSubmitting}
              size="xl"
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                "Continue to Payment"
              )}
            </Button>
          </div>
        )}

        <div className={showSubmitButton ? "mt-4" : "mt-2"}>
          <div className="flex flex-col gap-2">
            <TrustLine text="Secure payment processing" />
            <TrustLine text="Live inventory validation" />
            <TrustLine text="Confirmation by email" />
            {hasFlight && (
              <div className="flex items-center gap-2">
                <div className="flex size-4 items-center justify-center rounded-full bg-amber-400/15">
                  <Info className="size-2.5 text-amber-400" />
                </div>
                <span className="text-xs text-muted-foreground">
                  Flight price valid at time of booking and subject to change
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function SurfaceCard({
  title,
  icon,
  children,
  headingStyle = "default",
}: {
  title: string
  icon?: ReactNode
  children: ReactNode
  headingStyle?: "default" | "compact"
}) {
  return (
    <div className="rounded-xl border border-border bg-muted p-4 @min-[48rem]:p-6">
      {headingStyle === "compact" ? (
        <h2 className="text-base font-bold tracking-wider text-foreground uppercase">
          {title}
        </h2>
      ) : (
        <div className="mb-4 flex items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold text-foreground">
            {title}
          </h2>
        </div>
      )}
      {children}
    </div>
  )
}

function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  )
}

function SummaryRow({
  label,
  value,
  muted = false,
}: {
  label: ReactNode
  value: string
  muted?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          muted
            ? "text-sm text-muted-foreground"
            : "text-sm text-muted-foreground"
        }
      >
        {label}
      </span>
      <span
        className={
          muted
            ? "text-sm font-semibold text-foreground"
            : "text-sm font-semibold text-foreground"
        }
      >
        {value}
      </span>
    </div>
  )
}

function PaymentBadge({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-[72px] items-center justify-center gap-1.5 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-xs font-semibold text-foreground">
      {children}
    </div>
  )
}

function TrustLine({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex size-4 items-center justify-center rounded-full bg-primary/15">
        <Check className="size-2.5 text-primary" />
      </div>
      <span className="text-xs text-muted-foreground">{text}</span>
    </div>
  )
}

function PackageIcon({
  packageType,
}: {
  packageType: "flight-hotel" | "hotel-only" | "flight-only"
}) {
  return (
    <>
      {packageType !== "flight-only" && (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M4.66667 8.66667C5.77333 8.66667 6.66667 7.77333 6.66667 6.66667C6.66667 5.56 5.77333 4.66667 4.66667 4.66667C3.56 4.66667 2.66667 5.56 2.66667 6.66667C2.66667 7.77333 3.56 8.66667 4.66667 8.66667ZM12.6667 4.66667H8.66667C7.93333 4.66667 7.33333 5.26667 7.33333 6V9.33333H2V4C2 3.63333 1.7 3.33333 1.33333 3.33333C0.966667 3.33333 0.666667 3.63333 0.666667 4V12.6667C0.666667 13.0333 0.966667 13.3333 1.33333 13.3333C1.7 13.3333 2 13.0333 2 12.6667V11.3333H14V12.6667C14 13.0333 14.3 13.3333 14.6667 13.3333C15.0333 13.3333 15.3333 13.0333 15.3333 12.6667V7.33333C15.3333 5.86 14.14 4.66667 12.6667 4.66667Z"
            fill="var(--primary)"
          />
        </svg>
      )}
      {(packageType === "flight-hotel" || packageType === "flight-only") && (
        <svg width="16" height="16" viewBox="0 0 13.3333 13.0994" fill="none">
          <path
            d="M5.33333 7.86667L1.11667 9.55C0.85 9.66111 0.597333 9.63622 0.358667 9.47533C0.12 9.31444 0.000444444 9.08933 0 8.8V8.43333C0 8.3 0.0306667 8.17222 0.092 8.05C0.153333 7.92778 0.239333 7.82778 0.35 7.75L5.33333 4.26667V1.33333C5.33333 0.966668 5.464 0.65289 5.72533 0.392001C5.98667 0.131112 6.30044 0.000445574 6.66667 1.12994e-06C7.03289 -0.000443314 7.34689 0.130223 7.60867 0.392001C7.87044 0.653779 8.00089 0.967557 8 1.33333V4.26667L12.9833 7.75C13.0944 7.82778 13.1807 7.92778 13.242 8.05C13.3033 8.17222 13.3338 8.3 13.3333 8.43333V8.8C13.3333 9.08889 13.214 9.314 12.9753 9.47533C12.7367 9.63667 12.4838 9.66156 12.2167 9.55L8 7.86667V10.2667L9.71667 11.4667C9.80556 11.5333 9.87511 11.614 9.92533 11.7087C9.97556 11.8033 10.0004 11.906 10 12.0167V12.4167C10 12.6389 9.90822 12.8196 9.72467 12.9587C9.54111 13.0978 9.33844 13.1338 9.11667 13.0667L6.66667 12.3333L4.21667 13.0667C3.99444 13.1333 3.79156 13.0973 3.608 12.9587C3.42444 12.82 3.33289 12.6393 3.33333 12.4167V12.0167C3.33333 11.9056 3.35844 11.8029 3.40867 11.7087C3.45889 11.6144 3.52822 11.5338 3.61667 11.4667L5.33333 10.2667V7.86667Z"
            fill="var(--primary)"
          />
        </svg>
      )}
    </>
  )
}

function ConsentCheckboxes({
  subscribe,
  setSubscribe,
  termsAccepted,
  setTermsAccepted,
  termsError,
  setTermsError,
  termsHref,
  privacyHref,
}: {
  subscribe: boolean
  setSubscribe: (v: boolean) => void
  termsAccepted: boolean
  setTermsAccepted: (v: boolean) => void
  termsError: boolean
  setTermsError: (v: boolean) => void
  termsHref: string
  privacyHref: string
}) {
  return (
    <div className="flex flex-col gap-3 py-2">
      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <Checkbox
          checked={subscribe}
          onCheckedChange={() => setSubscribe(!subscribe)}
        />
        <span>I agree to be contacted about special offers and promotions via my email address and/or phone number.</span>
      </label>

      <div>
        <label className="flex items-start gap-3 text-sm text-muted-foreground">
          <Checkbox
            checked={termsAccepted}
            onCheckedChange={() => {
              setTermsAccepted(!termsAccepted)
              if (!termsAccepted) setTermsError(false)
            }}
            aria-invalid={termsError || undefined}
          />
          <span>
            I agree to the{" "}
            <a href={termsHref} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">
              Terms &amp; Conditions
            </a>{" "}
            and acknowledge the{" "}
            <a href={privacyHref} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">
              Privacy Policy
            </a>
            , as well as the terms and conditions of all service providers included in my order.
            <span className="ml-0.5 text-destructive">*</span>
          </span>
        </label>
        {termsError && (
          <p className="mt-1.5 pl-7 text-xs font-medium text-destructive">
            You must agree to the Terms &amp; Conditions to continue.
          </p>
        )}
      </div>
    </div>
  )
}
